import { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Calendar, MapPin, Users, GraduationCap, FileText, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

const DigitalConcierge = () => {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [currentScreen, setCurrentScreen] = useState('main');

    const togglePanel = (e) => {
        e?.stopPropagation();
        setIsOpen(prev => !prev);
        if (!isOpen) {
            setCurrentScreen('main');
        }
    };

    const goBack = () => {
        setCurrentScreen('main');
    };

    const handleNavigate = (path) => {
        setIsOpen(false);
        navigate(path);
    };

    // Content based on language
    const content = {
        title: language === 'tr' ? 'Girne MUN 2026 Concierge' : 'Girne MUN 2026 Concierge',
        welcome: language === 'tr' 
            ? 'Merhaba! Size nasıl yardımcı olabilirim?' 
            : 'Welcome! How can I assist you today?',
        options: {
            learnMUN: language === 'tr' ? 'MUN Hakkında' : 'Learn About MUN',
            applyNow: language === 'tr' ? 'Başvur' : 'Apply Now',
            eventDetails: language === 'tr' ? 'Etkinlik Detayları' : 'Event Details'
        },
        munInfo: {
            title: language === 'tr' ? 'Model Birleşmiş Milletler' : 'Model United Nations',
            desc: language === 'tr' 
                ? 'MUN, öğrencilerin BM delegelerini simüle ettiği, diplomasi ve müzakere becerilerini geliştirdiği akademik bir konferanstır.'
                : 'MUN is an academic conference where students simulate UN delegates, developing diplomacy and negotiation skills.',
            committees: language === 'tr' ? '9 Farklı Komite' : '9 Different Committees',
            experience: language === 'tr' ? 'Unutulmaz Deneyim' : 'Unforgettable Experience',
            learnMore: language === 'tr' ? 'Daha Fazla Bilgi' : 'Learn More'
        },
        apply: {
            title: language === 'tr' ? 'Başvuru' : 'Application',
            delegates: '500+',
            delegatesLabel: language === 'tr' ? 'Beklenen Delege' : 'Expected Delegates',
            joinText: language === 'tr' 
                ? "Türkiye'nin dört bir yanından gelen delegelere katılın!" 
                : 'Join delegates from all over Turkey!',
            continueBtn: language === 'tr' ? 'Başvuruya Devam Et' : 'Continue to Application',
            committeesBtn: language === 'tr' ? 'Önce Komiteleri Gör' : 'See Committees First'
        },
        event: {
            title: language === 'tr' ? 'Etkinlik Detayları' : 'Event Details',
            date: '27-28-29 March 2026',
            location: 'Adana – Girne College Campus',
            capacity: '500+',
            capacityLabel: language === 'tr' ? 'Delege Kapasitesi' : 'Delegate Capacity',
            secureBtn: language === 'tr' ? 'Yerinizi Ayırın' : 'Secure Your Seat'
        },
        back: language === 'tr' ? 'Geri' : 'Back'
    };

    return (
        <>
            {/* Floating Button */}
            <button
                onClick={togglePanel}
                data-testid="concierge-toggle-btn"
                className={cn(
                    "fixed z-[9999] cursor-pointer",
                    "transition-all duration-300 ease-out",
                    "hover:scale-110",
                    "animate-pulse-slow pointer-events-auto",
                    isOpen && "scale-0 opacity-0 pointer-events-none"
                )}
                aria-label="Open Concierge"
                style={{
                    right: '20px',
                    bottom: '20px',
                    width: '70px',
                    height: '70px',
                    background: '#ffffff',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                    border: 'none',
                    pointerEvents: isOpen ? 'none' : 'auto'
                }}
            >
                <img 
                    src="/logo.png"
                    alt="Girne MUN"
                    style={{
                        width: '85%',
                        height: '85%',
                        objectFit: 'contain',
                        borderRadius: '50%'
                    }}
                />
            </button>

            {/* Backdrop */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black/30 z-[9998] backdrop-blur-sm"
                    onClick={togglePanel}
                    data-testid="concierge-backdrop"
                />
            )}

            {/* Panel */}
            <div 
                className="fixed top-0 right-0 h-full w-full sm:w-96 z-[9999] transition-transform duration-500 ease-out"
                style={{ 
                    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                    pointerEvents: isOpen ? 'auto' : 'none'
                }}
                data-testid="concierge-panel"
            >
                <div className="h-full bg-primary/80 backdrop-blur-xl border-l border-accent/20 shadow-2xl flex flex-col">
                    {/* Header */}
                    <div className="p-6 border-b border-accent/20">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                                    <img 
                                        src="/logo.png"
                                        alt="Girne MUN"
                                        className="w-6 h-6 object-contain"
                                    />
                                </div>
                                <h2 className="font-display text-lg font-semibold text-primary-foreground">
                                    {content.title}
                                </h2>
                            </div>
                            <button
                                onClick={togglePanel}
                                className="p-2 rounded-full hover:bg-accent/20 text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {/* Main Screen */}
                        {currentScreen === 'main' && (
                            <div className="space-y-6 animate-fadeIn">
                                <p className="text-primary-foreground/80 text-sm">
                                    {content.welcome}
                                </p>

                                <div className="space-y-3">
                                    {/* Learn About MUN */}
                                    <button
                                        onClick={() => setCurrentScreen('mun')}
                                        className="w-full p-4 rounded-xl bg-accent/10 border border-accent/20 hover:bg-accent/20 transition-all duration-300 flex items-center justify-between group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                                                <GraduationCap className="w-5 h-5 text-accent" />
                                            </div>
                                            <span className="font-medium text-primary-foreground">
                                                {content.options.learnMUN}
                                            </span>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-accent group-hover:translate-x-1 transition-transform" />
                                    </button>

                                    {/* Apply Now */}
                                    <button
                                        onClick={() => setCurrentScreen('apply')}
                                        className="w-full p-4 rounded-xl bg-accent/10 border border-accent/20 hover:bg-accent/20 transition-all duration-300 flex items-center justify-between group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                                                <FileText className="w-5 h-5 text-accent" />
                                            </div>
                                            <span className="font-medium text-primary-foreground">
                                                {content.options.applyNow}
                                            </span>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-accent group-hover:translate-x-1 transition-transform" />
                                    </button>

                                    {/* Event Details */}
                                    <button
                                        onClick={() => setCurrentScreen('event')}
                                        className="w-full p-4 rounded-xl bg-accent/10 border border-accent/20 hover:bg-accent/20 transition-all duration-300 flex items-center justify-between group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                                                <Info className="w-5 h-5 text-accent" />
                                            </div>
                                            <span className="font-medium text-primary-foreground">
                                                {content.options.eventDetails}
                                            </span>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-accent group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* MUN Info Screen */}
                        {currentScreen === 'mun' && (
                            <div className="space-y-6 animate-fadeIn">
                                <button
                                    onClick={goBack}
                                    className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors text-sm"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    {content.back}
                                </button>

                                <div className="text-center py-4">
                                    <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                                        <GraduationCap className="w-8 h-8 text-accent" />
                                    </div>
                                    <h3 className="font-display text-xl font-bold text-primary-foreground mb-3">
                                        {content.munInfo.title}
                                    </h3>
                                    <p className="text-primary-foreground/70 text-sm leading-relaxed">
                                        {content.munInfo.desc}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-4 rounded-xl bg-accent/10 border border-accent/20 text-center">
                                        <p className="font-display text-2xl font-bold text-accent">9</p>
                                        <p className="text-xs text-primary-foreground/70 mt-1">
                                            {content.munInfo.committees}
                                        </p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-accent/10 border border-accent/20 text-center">
                                        <p className="font-display text-2xl font-bold text-accent">3</p>
                                        <p className="text-xs text-primary-foreground/70 mt-1">
                                            {language === 'tr' ? 'Gün' : 'Days'}
                                        </p>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleNavigate('/about')}
                                    className="w-full py-3 rounded-xl bg-accent text-primary font-semibold hover:bg-accent/90 transition-colors"
                                >
                                    {content.munInfo.learnMore}
                                </button>
                            </div>
                        )}

                        {/* Apply Screen */}
                        {currentScreen === 'apply' && (
                            <div className="space-y-6 animate-fadeIn">
                                <button
                                    onClick={goBack}
                                    className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors text-sm"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    {content.back}
                                </button>

                                <div className="text-center py-4">
                                    <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                                        <span className="font-display text-3xl font-bold text-accent">
                                            {content.apply.delegates}
                                        </span>
                                    </div>
                                    <p className="text-primary-foreground/70 text-sm">
                                        {content.apply.delegatesLabel}
                                    </p>
                                    <p className="text-primary-foreground text-base mt-4 font-medium">
                                        {content.apply.joinText}
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    <button
                                        onClick={() => handleNavigate('/apply')}
                                        className="w-full py-3 rounded-xl bg-accent text-primary font-semibold hover:bg-accent/90 transition-colors"
                                    >
                                        {content.apply.continueBtn}
                                    </button>
                                    <button
                                        onClick={() => handleNavigate('/committees')}
                                        className="w-full py-3 rounded-xl bg-transparent border border-accent/50 text-accent font-medium hover:bg-accent/10 transition-colors"
                                    >
                                        {content.apply.committeesBtn}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Event Details Screen */}
                        {currentScreen === 'event' && (
                            <div className="space-y-6 animate-fadeIn">
                                <button
                                    onClick={goBack}
                                    className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors text-sm"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    {content.back}
                                </button>

                                <h3 className="font-display text-xl font-bold text-primary-foreground text-center">
                                    {content.event.title}
                                </h3>

                                <div className="space-y-4">
                                    {/* Date */}
                                    <div className="p-4 rounded-xl bg-accent/10 border border-accent/20 flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                                            <Calendar className="w-6 h-6 text-accent" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-primary-foreground/60 uppercase tracking-wider">
                                                {language === 'tr' ? 'Tarih' : 'Date'}
                                            </p>
                                            <p className="font-display font-semibold text-primary-foreground">
                                                {content.event.date}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Location */}
                                    <div className="p-4 rounded-xl bg-accent/10 border border-accent/20 flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                                            <MapPin className="w-6 h-6 text-accent" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-primary-foreground/60 uppercase tracking-wider">
                                                {language === 'tr' ? 'Konum' : 'Location'}
                                            </p>
                                            <p className="font-display font-semibold text-primary-foreground">
                                                {content.event.location}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Capacity */}
                                    <div className="p-4 rounded-xl bg-accent/10 border border-accent/20 flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                                            <Users className="w-6 h-6 text-accent" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-primary-foreground/60 uppercase tracking-wider">
                                                {content.event.capacityLabel}
                                            </p>
                                            <p className="font-display font-semibold text-primary-foreground">
                                                {content.event.capacity}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleNavigate('/apply')}
                                    className="w-full py-3 rounded-xl bg-accent text-primary font-semibold hover:bg-accent/90 transition-colors"
                                >
                                    {content.event.secureBtn}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-accent/20 text-center">
                        <p className="text-xs text-primary-foreground/50">
                            Girne MUN 2026 • Digital Concierge
                        </p>
                    </div>
                </div>
            </div>

            {/* Custom Styles */}
            <style>{`
                @keyframes pulse-slow {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(201, 162, 39, 0.4); }
                    50% { box-shadow: 0 0 0 12px rgba(201, 162, 39, 0); }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 2s ease-in-out infinite;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out;
                }
            `}</style>
        </>
    );
};

export default DigitalConcierge;
